import {
  Form,
  FormInput,
  FormField,
  Button,
  FormTextarea,
  FormItem,
  FormLabel,
  FormControl,
  cn,
  Input,
} from "web-design-system";
import { imageAssetUrl } from "@/utils/url";
import { useForm } from "react-hook-form";
import { EditIcon, UploadIcon } from "lucide-react";
import { lazy, Suspense, useEffect, useState } from "react";
import {
  useDistributorBranding,
  useDistributorProfile,
  useUpdateDistributorBranding,
  useUpdateDistributorProfile,
} from "creso-core/hooks";
import { distributorBrandingSchema } from "creso-core/zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { DistributorBrandingFormValues } from "creso-core/types";
import { handleError } from "@/utils/error";
import { toast } from "sonner";
import { CopyButton } from "@core/components/copy-btn";
import { useAuthContext } from "@/providers/auth-provider";
import { isEmployeeUser } from "creso-core/utils";

const DownloadVCard = lazy(() => import("../components/vcard"));

export function LegacyBranding() {
  const { data: branding } = useDistributorBranding();
  const { data: profile } = useDistributorProfile();
  const { mutateAsync: updateDistributorBranding, isPending: isUpdating } =
    useUpdateDistributorBranding();
  const { employee, user } = useAuthContext();
  const { mutateAsync: updateDistributorProfile } =
    useUpdateDistributorProfile();
  const isEmployee = isEmployeeUser(user.user_type);
  const onboardingUrl = `${window.location.origin}/mfd/${profile?.handle}${isEmployee && employee?.id ? `?employee_id=${employee?.id}` : ""}`;

  const [logoPreview, setLogoPreview] = useState<string | null>(null);

  const form = useForm<DistributorBrandingFormValues>({
    values: {
      brand_name: branding?.brand_name || "",
      tag_line: branding?.tag_line || "",
      profile_bio: branding?.profile_bio || "",
      brand_logo: branding?.brand_logo || "",
      brand_qr: branding?.brand_qr || "",
      id: branding?.id || 0,
      distributor: branding?.distributor || 0,
    },
    resolver: zodResolver(distributorBrandingSchema),
  });

  const { isDirty } = form.formState;

  const onSubmit = async (data: DistributorBrandingFormValues) => {
    const formData = new FormData();

    Object.keys(form.formState.dirtyFields).forEach((key) => {
      const value = data[key as keyof DistributorBrandingFormValues];
      if (key === "brand_logo" && (value as unknown) instanceof File) {
        formData.append(key, value as File);
      } else {
        formData.append(key, String(value));
      }
    });

    try {
      await updateDistributorBranding(formData);
      toast.success("Profile updated successfully");
    } catch (error) {
      handleError(error, "Failed to update branding information");
    }
  };

  const [isEditingHandle, setIsEditingHandle] = useState(false);
  const [handle, setHandle] = useState(profile?.handle);
  const [handleUpdateError, setHandleUpdateError] = useState("");

  useEffect(() => {
    setHandle(profile?.handle || "");
  }, [profile?.handle]);

  useEffect(() => {
    setHandleUpdateError("");
  }, [handle]);

  const updateHandle = async () => {
    try {
      if (!handle) {
        setHandleUpdateError("URL is required");
        return;
      }
      if (!/^[a-zA-Z0-9\\-]+$/.test(handle)) {
        setHandleUpdateError(
          "URL is invalid, only alphanumeric characters and - are allowed"
        );
        return;
      }
      await updateDistributorProfile({ handle });
      setIsEditingHandle(false);
    } catch {
      setHandleUpdateError("URL not available");
    }
  };

  const handleLogoUpload = (file: File) => {
    const preview = URL.createObjectURL(file);
    setLogoPreview(preview);
    form.setValue("brand_logo", file as string & File, { shouldDirty: true });
  };

  return (
    <div className="size-full p-5 flex flex-col max-w-5xl">
      <h4>Branding</h4>
      <div className="h-px w-full bg-border mt-3" />
      <div className="space-y-6 mt-4 flex-1">
        <Form {...form}>
          <form
            className="flex flex-col size-full gap-6"
            onSubmit={form.handleSubmit(onSubmit)}
          >
            <div className="flex items-center gap-6">
              <FormField
                control={form.control}
                className="flex-1"
                name="brand_name"
                render={({ field }) => (
                  <FormInput {...field} label="Brand Name" />
                )}
              />
              <FormField
                control={form.control}
                className="flex-1"
                name="tag_line"
                render={({ field }) => (
                  <FormInput {...field} label="Tag line" />
                )}
              />
            </div>

            <div className="flex gap-3 items-center">
              <FormField
                control={form.control}
                name="brand_logo"
                render={({ field }) => (
                  <FormItem className="flex-1">
                    <FormLabel>Brand Logo</FormLabel>
                    <FormControl>
                      <div
                        className={cn(
                          "h-30 w-30 group rounded-lg relative",
                          !logoPreview &&
                            !branding?.brand_logo &&
                            "border border-dashed border-input"
                        )}
                      >
                        {(logoPreview || branding?.brand_logo) && (
                          <img
                            src={
                              logoPreview ?? (branding?.brand_logo as string)
                            }
                            alt="brand-logo"
                            className="h-30 w-30 object-cover group-hover:opacity-50 rounded-lg transition-all duration-300"
                          />
                        )}
                        <UploadIcon
                          className={cn(
                            "absolute stroke-foreground/50 size-6 top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity duration-300",
                            !logoPreview &&
                              !branding?.brand_logo &&
                              "opacity-100"
                          )}
                        />
                        <Input
                          {...field}
                          value=""
                          className="absolute size-full opacity-0 inset-0"
                          type="file"
                          accept="image/*"
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) {
                              handleLogoUpload(file);
                            }
                          }}
                        />
                      </div>
                    </FormControl>
                  </FormItem>
                )}
              />
              <FormField
                className="flex-1"
                control={form.control}
                name="profile_bio"
                render={({ field }) => (
                  <FormTextarea
                    {...field}
                    className="min-h-30"
                    label="Profile Bio"
                  />
                )}
              />
            </div>

            <div className="flex gap-3 justify-between">
              <div className="flex flex-1 justify-between rounded-lg bg-gradient-to-b from-slate-700 to-slate-900 text-white">
                <div className="p-4 w-fit flex flex-col justify-between">
                  <div className="flex flex-col gap-1">
                    <h5>Stand out</h5>
                    <h4>Share your brand with a digital touch.</h4>
                  </div>
                  <Suspense fallback={null}>
                    <DownloadVCard />
                  </Suspense>
                </div>
                <img
                  src={imageAssetUrl("card-hand.webp")}
                  alt="card-hand"
                  className="max-h-40"
                />
              </div>
              <div className="h-full bg-muted rounded-lg flex flex-col items-center justify-center">
                <img
                  className="w-40 h-40 object-contain"
                  src={`${branding?.brand_qr}?q=${branding?.modified_at}`}
                  alt="brand-qr"
                />
              </div>
            </div>

            <div className="flex flex-col gap-2">
              <span className="text-sm font-medium">
                Share client onboarding link
              </span>
              <div className="w-full flex items-start justify-between gap-2">
                <div className="flex flex-col gap-1 flex-1">
                  <div className="flex flex-row text-sm border rounded-md items-center px-2 border-input focus-within:border-primary">
                    <span className="flex-shrink-0 text-muted-foreground">{`${window.location.origin}/mfd/`}</span>
                    <Input
                      value={handle}
                      disabled={!isEditingHandle}
                      className={cn(
                        "border-0 px-0 !ring-0 !outline-none !opacity-100 !bg-background",
                        !isEditingHandle && "text-muted-foreground"
                      )}
                      onChange={(e) => setHandle(e.target.value)}
                    />
                  </div>
                  {isEditingHandle ? (
                    handleUpdateError ? (
                      <span className="text-destructive text-xs">
                        {handleUpdateError}
                      </span>
                    ) : (
                      <span className="text-muted-foreground text-xs">
                        Once updated, your previous link will stop working.
                      </span>
                    )
                  ) : null}
                </div>
                {!isEditingHandle ? (
                  <>
                    <Button
                      onClick={() => setIsEditingHandle(true)}
                      size="icon"
                      variant="outline"
                      type="button"
                    >
                      <EditIcon />
                    </Button>
                    <CopyButton text={onboardingUrl} />
                  </>
                ) : (
                  <>
                    <Button type="button" onClick={updateHandle}>
                      Save
                    </Button>
                    <Button
                      onClick={() => {
                        setHandle(profile?.handle);
                        setIsEditingHandle(false);
                      }}
                      type="button"
                      variant="outline"
                    >
                      Cancel
                    </Button>
                  </>
                )}
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-auto pb-4">
              <Button
                size="lg"
                variant="outline"
                type="button"
                onClick={() => {
                  form.reset();
                  setLogoPreview(null);
                }}
                disabled={isUpdating || !isDirty}
              >
                Cancel
              </Button>
              <Button type="submit" size="lg" disabled={isUpdating || !isDirty}>
                {isUpdating ? "Saving..." : "Save"}
              </Button>
            </div>
          </form>
        </Form>
      </div>
    </div>
  );
}
